import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { WALLS } from '../types/lanesAndDrivers';
import { supabase } from '../lib/supabase';

interface ProfileData {
  core_value_1: string | null;
  core_value_2: string | null;
  core_value_3: string | null;
  lane_1: string | null;
  lane_2: string | null;
  lane_3: string | null;
  lane_4: string | null;
  value_1_description: string | null;
  value_2_description: string | null;
  value_3_description: string | null;
  lane_1_description: string | null;
  lane_2_description: string | null;
  lane_3_description: string | null;
  lane_4_description: string | null;
}

export function ClarifyDefinitions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [definitions, setDefinitions] = useState({
    value_1_description: '',
    value_2_description: '',
    value_3_description: '',
    lane_1_description: '',
    lane_2_description: '',
    lane_3_description: '',
    lane_4_description: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (data) {
      const hasWalls = data.lane_1 && data.lane_2 && data.lane_3 && data.lane_4;

      if (!hasWalls) {
        navigate('/your-walls');
        return;
      }

      setProfile(data);
      setDefinitions({
        value_1_description: data.value_1_description || '',
        value_2_description: data.value_2_description || '',
        value_3_description: data.value_3_description || '',
        lane_1_description: data.lane_1_description || '',
        lane_2_description: data.lane_2_description || '',
        lane_3_description: data.lane_3_description || '',
        lane_4_description: data.lane_4_description || '',
      });
    }
  }

  function getWallName(wallId: string | null): string {
    if (!wallId) return '';
    return WALLS.find(w => w.id === wallId)?.name || wallId;
  }

  function handleDefinitionChange(key: keyof typeof definitions, value: string) {
    setDefinitions(prev => ({ ...prev, [key]: value }));
  }

  async function handleContinue() {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('profiles')
        .update(definitions)
        .eq('id', user.id);

      if (error) throw error;

      navigate('/ai-spark-prompt');
    } catch (error) {
      console.error('Error saving definitions:', error);
      alert('Failed to save your definitions. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!profile) {
    return (
      <ProtectedLayout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </ProtectedLayout>
    );
  }

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Clarify Your Definitions
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Take a moment to define what each of your values and walls means to you personally.
              These definitions will be used to create your custom AI Prompt.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Core Values</h2>
                <div className="space-y-4">
                  {profile.core_value_1 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {profile.core_value_1} means
                      </label>
                      <textarea
                        value={definitions.value_1_description}
                        onChange={(e) => handleDefinitionChange('value_1_description', e.target.value)}
                        placeholder="Describe what this value means to you..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                  {profile.core_value_2 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {profile.core_value_2} means
                      </label>
                      <textarea
                        value={definitions.value_2_description}
                        onChange={(e) => handleDefinitionChange('value_2_description', e.target.value)}
                        placeholder="Describe what this value means to you..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                  {profile.core_value_3 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {profile.core_value_3} means
                      </label>
                      <textarea
                        value={definitions.value_3_description}
                        onChange={(e) => handleDefinitionChange('value_3_description', e.target.value)}
                        placeholder="Describe what this value means to you..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Walls</h2>
                <div className="space-y-4">
                  {profile.lane_1 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {getWallName(profile.lane_1)} is
                      </label>
                      <textarea
                        value={definitions.lane_1_description}
                        onChange={(e) => handleDefinitionChange('lane_1_description', e.target.value)}
                        placeholder="Describe what this wall means to you..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                  {profile.lane_2 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {getWallName(profile.lane_2)} is
                      </label>
                      <textarea
                        value={definitions.lane_2_description}
                        onChange={(e) => handleDefinitionChange('lane_2_description', e.target.value)}
                        placeholder="Describe what this wall means to you..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                  {profile.lane_3 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {getWallName(profile.lane_3)} is
                      </label>
                      <textarea
                        value={definitions.lane_3_description}
                        onChange={(e) => handleDefinitionChange('lane_3_description', e.target.value)}
                        placeholder="Describe what this wall means to you..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                  {profile.lane_4 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {getWallName(profile.lane_4)} is
                      </label>
                      <textarea
                        value={definitions.lane_4_description}
                        onChange={(e) => handleDefinitionChange('lane_4_description', e.target.value)}
                        placeholder="Describe what this wall means to you..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Tip:</strong> These definitions are optional, but they help create a more personalized AI prompt.
                You can always skip this and come back later to refine them.
              </p>
            </div>
          </div>

          <div className="flex justify-between">
            <Button
              onClick={() => navigate('/your-walls')}
              variant="secondary"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            <Button
              onClick={handleContinue}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Continue'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
